/** QuickTicketing — Pièces jointes : sélection (coller, glisser, parcourir) et affichage. */
import React, { useRef, useState } from 'react';
import { prepareFile, filesOf, MAX_FILES } from '../attachments.js';
import { fileUrl } from '../api/client.js';

/** Hook : liste de pièces jointes en préparation + gestionnaires coller / déposer. */
export function useAttachments() {
  const [files, setFiles] = useState([]);
  const [error, setError] = useState(null);
  const add = async (list) => {
    const raw = filesOf(list);
    if (!raw.length) return false;
    setError(null);
    for (const f of raw) {
      try {
        const prepared = await prepareFile(f);
        setFiles((cur) => (cur.length >= MAX_FILES ? cur : [...cur, prepared]));
      } catch (e) {
        setError(e.message);
      }
    }
    return true;
  };
  const onPaste = (e) => {
    if (filesOf(e.clipboardData?.items).length) { e.preventDefault(); add(e.clipboardData.items); }
  };
  const remove = (id) => setFiles((cur) => cur.filter((f) => f.id !== id));
  const clear = () => setFiles([]);
  return { files, error, add, onPaste, remove, clear };
}

/** Zone de dépôt + vignettes. */
export function AttachmentPicker({ att, hint = 'Collez une capture (Ctrl+V), glissez des fichiers ou' }) {
  const input = useRef(null);
  const [over, setOver] = useState(false);
  return (
    <div
      className={`dropzone${over ? ' over' : ''}`}
      onDragOver={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => { e.preventDefault(); setOver(false); att.add(e.dataTransfer.files); }}
    >
      <div className="row wrap">
        <span className="muted">{hint}</span>
        <button type="button" onClick={() => input.current?.click()}>📎 Parcourir…</button>
        <span className="muted">{att.files.length}/{MAX_FILES}</span>
        <input ref={input} type="file" multiple hidden onChange={(e) => { att.add(e.target.files); e.target.value = ''; }} />
      </div>
      {att.error && <div className="error-line">{att.error}</div>}
      {att.files.length > 0 && (
        <div className="thumbs">
          {att.files.map((f) => (
            <div key={f.id} className="thumb">
              {f.preview ? <img src={f.preview} alt={f.name} /> : <div className="thumb-file">📄 {f.name}</div>}
              <button type="button" className="icon thumb-x" title="Retirer" onClick={() => att.remove(f.id)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const isImage = (a) => /^image\/(png|jpeg|gif|webp|bmp)$/.test(a.mediaType);
const size = (n) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} Mo` : `${Math.max(1, Math.round(n / 1024))} Ko`);

/** Pièces jointes d'un ticket (ou d'un événement) : vignettes cliquables + fichiers. */
export function AttachmentList({ ticketId, items, onZoom }) {
  if (!items?.length) return null;
  return (
    <div className="thumbs">
      {items.map((a) => (isImage(a) ? (
        <button key={a.file} type="button" className="thumb as-button" title={a.name} onClick={() => onZoom?.(fileUrl(ticketId, a.file))}>
          <img src={fileUrl(ticketId, a.file)} alt={a.name} loading="lazy" />
        </button>
      ) : (
        <a key={a.file} className="thumb-file link-file" href={fileUrl(ticketId, a.file)} download={a.name}>
          📄 {a.name} <span className="muted">{size(a.size)}</span>
        </a>
      )))}
    </div>
  );
}
